import { Component, OnInit, OnDestroy, Input } from "@angular/core";
import { Store, Action } from "@ngrx/store";
import { Actions } from "@ngrx/effects";
import { AppState } from "@store/app-state";
import { Subscription } from "rxjs/Subscription";
import { TransactionsStore } from "@store/transactions";
import { Contact } from "@models/contact";
import { Transaction } from "@models/transaction";
import { LocalData, CompleterService, CompleterData, CompleterItem } from 'ng2-completer'
import { TRANSACTION_TYPES, PAYMENT_TYPES } from "@constants/transactions";
import { ConstData } from "@constants/helpers";
import { Utilities } from "@helpers/utilities";
import {
  CREATE_TRANSACTION,
  CREATED_TRANSACTION,
  CREATE_TRANSACTION_FAILURE,
  FETCH_POTENTIAL_TRANSACTIONS,
  CREATED_TRANSACTION_TRANSFER,
  CREATE_TRANSACTION_TRANSFER,
  RECORD_OTHER_PAYMENT,
  RECORDED_OTHER_PAYMENT
} from "@reducers/transactions";
import { ModalService } from "@services/modal";
import { FetchTransactionParams, TransferTransactionParams } from "@services/transaction";

@Component({
  selector: 'create-transaction-form',
  templateUrl: './create-transaction-form.pug',
})
export class CreateTransactionFormComponent implements OnInit, OnDestroy {

  private transactionsSubscription: Subscription

  public feeTypeSearchStr: string
  public feeTypesCompleter: CompleterData

  @Input() public recordType: string
  public debtor: Contact
  public transaction: Transaction = new Transaction()
  public transactionDateInputStr: string
  public potentialTransactions: Transaction[] = []
  public preselectedTransaction: Transaction = null
  public amount: number = 0
  public transferTransactionParams: TransferTransactionParams = {}

  public isLoading: boolean = false

  constructor(
    private store: Store<AppState>,
    private actions: Actions,
    private completerService: CompleterService,
    private modalService: ModalService,
  ) {}

  ngOnInit() {
    this.transactionsSubscription =
      this.store.select("transactionsReducer")
        .subscribe((s: TransactionsStore) => {
          this.debtor                 = s.activeDebtor
          this.potentialTransactions  = s.potentialTransactions
          this.preselectedTransaction = s.activeTransaction
        })

    const feeTypes = TRANSACTION_TYPES.filter((t) => [1, 2, 3].includes(t.value))
    this.feeTypesCompleter =
      this.completerService.local(feeTypes, "title", "title")

    this.setInitialState()
  }

  setInitialState() {
    this.transaction.debtorId = this.debtor.id
    this.transaction.transactionDate = new Date()
    this.transactionDateInputStr =
      Utilities.getDateInInputStringFormat(this.transaction.transactionDate)

    switch (this.recordType) {
      case 'fee':
        this.transaction.studentId = this.debtor.students[0].id
        this.transaction.paymentType =
          PAYMENT_TYPES.find((t) => t.title == "Direct Debit").value
        break
      case 'payment':
        this.transaction.transactionType =
          TRANSACTION_TYPES.find((t) => t.title == "Record Payment").value
        this.transaction.paymentType =
          PAYMENT_TYPES.find((t) => t.title == "Direct Debit").value
        this.fetchPotentialTransactions()
        break
      case 'credit':
        this.transaction.transactionType =
          TRANSACTION_TYPES.find((t) => t.title == "Record Credit").value
        this.fetchPotentialTransactions()
        break
      case 'waive':
        this.transaction.transactionType =
          TRANSACTION_TYPES.find((t) => t.title == "Waive").value
        this.transaction.amount = this.preselectedTransaction.amount
        this.transaction.parentId = this.preselectedTransaction.id
        break
      case 'refund':
        this.transaction.transactionType =
          TRANSACTION_TYPES.find((t) => t.title == "Refund").value
        this.transaction.amount = this.preselectedTransaction.amount * -1
        this.transaction.parentId = this.preselectedTransaction.id
        break
      case 'transfer':
        this.transaction.transactionType =
          TRANSACTION_TYPES.find((t) => t.title == "Transfer").value
        this.transaction.amount = this.preselectedTransaction.amount * -1
        this.transferTransactionParams.amount = this.preselectedTransaction.amount * -1
        this.transferTransactionParams.from_tx_id = this.preselectedTransaction.id
        this.fetchPotentialTransactions()
        break
      case 'record-other-payment':
        this.transaction.amount = this.preselectedTransaction.amount * -1
        this.transaction.parentId = this.preselectedTransaction.id
        break
    }
  }

  fetchPotentialTransactions() {
    const payload: FetchTransactionParams = {
      debtor_id: this.debtor.id,
      has_owing: true,
    }
    this.store.dispatch({ type: FETCH_POTENTIAL_TRANSACTIONS, payload })
  }

  getPaymentTypes(): ConstData[] {
    return PAYMENT_TYPES.filter((pt: ConstData) => {
      switch (this.recordType) {
        case 'fee':
          return [0, 1, 2].includes(pt.value)
        case 'payment':
          return [0, 1].includes(pt.value)
        default:
          return []
      }
    })
  }

  selectedFeeType(e: CompleterItem) {
    if (e) {
      const v = parseInt(e.originalObject.value)
      this.transaction.transactionType = v
      switch (v) {
        case 1:
          this.setDescriptionForEnrolmentFee()
          this.transaction.amount = 55
          break
        case 2:
          this.transaction.description = "Late Fee"
          this.transaction.amount = 55
          break
        case 3:
          this.transaction.description = "Custom Fee"
          this.transaction.amount = 0
          break
        default:
          this.transaction.description = null
          this.transaction.amount = 0
          break
      }
    }
    else {
      this.transaction.paymentType  = null
      this.transaction.description  = null
      this.transaction.amount       = 0
    }
  }

  setDescriptionForEnrolmentFee() {
    const s = this.debtor.students.find((s) => {
      return s.id == this.transaction.studentId
    })
    this.transaction.description = `Enrolment Fee (${s.fullName})`
  }

  selectedTransactionDate(dateString: string) {
    this.transaction.transactionDate = new Date(dateString)
  }

  isStudentSelected(student: Contact) {
    return this.transaction.studentId == student.id
  }

  isPaymentTypeSelected(pt: ConstData) {
    return this.transaction.paymentType == pt.value
  }

  isPotentialTransactionSelected(t: Transaction) {
    return this.transaction.parentId == t.id ||
      this.transferTransactionParams.to_tx_id == t.id
  }

  isPaymentReferenceVisible() {
    return this.transaction.paymentType == PAYMENT_TYPES.find((pt) => {
      return pt.title == 'Other Payment'
    }).value
  }

  changedAmount(e: number) {
    if (this.recordType == 'transfer') {
      this.transferTransactionParams.amount = e
    }
  }

  clickedStudent(student: Contact) {
    this.transaction.studentId = student.id
    if (this.recordType == 'fee') {
      this.setDescriptionForEnrolmentFee()
    }
  }

  clickedPaymentType(pt: ConstData) {
    this.transaction.paymentType = pt.value
    if (pt != PAYMENT_TYPES.find((pt) => pt.title == 'Other Payment')) {
      this.transaction.paymentReference = null
    }
  }

  clickedPotentialTransaction(t: Transaction) {
    if (this.recordType == 'transfer') {
      this.transferTransactionParams.to_tx_id = t.id
    }
    else {
      this.transaction.parentId     = t.id
      this.transaction.amount       = t.amount
    }
  }

  isFormSectionVisible(section: string): boolean {
    switch (section) {
      case 'fee-type':
        return ['fee'].includes(this.recordType)

      case 'student':
        return ['fee'].includes(this.recordType)

      case 'transactions':
        return ['payment', 'credit', 'transfer'].includes(this.recordType)

      case 'description':
        return ['fee', 'credit'].includes(this.recordType)

      case 'amount':
        return ['fee', 'payment', 'credit', 'waive', 'refund', 'transfer', 'record-other-payment'].includes(this.recordType)

      case 'date':
        return ['fee', 'payment', 'credit'].includes(this.recordType)

      case 'payment-type':
        return ['fee', 'payment'].includes(this.recordType)

      case 'standalone-payment-reference':
        return ['record-other-payment'].includes(this.recordType)

      default:
        return false
    }
  }

  isFormValid(): boolean {
    const e     = this.potentialTransactions.find((t) => t.id == t.parentId)
    const t     = this.transaction
    let r       = true

    switch (this.recordType) {
      case 'fee':
        if (![1, 2, 3].includes(t.transactionType)) { r = false }
        if (!t.description) { r = false }
        break
      case 'payment':
        if (e && t.amount > e.amount) { r = false }
        if (!t.parentId) { r = false }
        if (!t.amount) { r = false }
        break
      case 'credit':
        if (e && t.amount > e.amount) { r = false }
        if (!t.amount) { r = false }
        if (!t.parentId) { r = false }
        if (!t.description) { r = false }
        break
      case 'waive':
        if (!t.amount) { r = false }
        if (t.amount > this.preselectedTransaction.amount) { r = false }
        break
      case 'refund':
        if (!t.amount) { r = false }
        if (t.amount > this.preselectedTransaction.amount * -1) { r = false }
        break
      case 'transfer':
        if (!this.transferTransactionParams.from_tx_id) { r = false }
        if (!this.transferTransactionParams.to_tx_id) { r = false }
        if (!this.transferTransactionParams.amount) { r = false }
        if (this.transferTransactionParams.amount > this.preselectedTransaction.amount * -1) {
          r = false
        }
        break
      case 'record-other-payment':
        if (!t.amount) { r = false }
        if (t.amount > this.preselectedTransaction.amount * -1) { r = false }
        if (!t.paymentReference) { r = false }
        if (!t.parentId) { r = false }
        break
    }

    if (t.paymentType == PAYMENT_TYPES.find((pt) => pt.title == "Other Payment").value
      && !t.paymentReference) { r = false }

    return r
  }

  submitForm() {
    const callback = () => {
      this.isLoading = false
      this.modalService.closeModal() }

    switch (this.recordType) {
      case 'transfer':
        this.actions
          .ofType(CREATED_TRANSACTION_TRANSFER)
          .take(1)
          .subscribe((a: Action) => { callback() })
        this.isLoading = true
        this.store.dispatch({ type: CREATE_TRANSACTION_TRANSFER, payload: this.transferTransactionParams })
        break
      case 'record-other-payment':
        this.actions
          .ofType(RECORDED_OTHER_PAYMENT)
          .take(1)
          .subscribe((a: Action) => { callback() })
        this.isLoading = true
        this.store.dispatch({ type: RECORD_OTHER_PAYMENT, payload: this.transaction })
        break
      default:
        this.actions
          .ofType(CREATED_TRANSACTION)
          .take(1)
          .subscribe((a: Action) => { callback() })
        this.isLoading = true
        this.store.dispatch({ type: CREATE_TRANSACTION, payload: this.transaction })
        break
    }
  }

  ngOnDestroy() {
    this.transactionsSubscription.unsubscribe()
  }

}



// WEBPACK FOOTER //
// /Users/jason/src/matrix/client/src/app/components/create-transaction-form.ts